/**
 * Reports → Call Reports → Call History.
 *
 * One row per call: who called whom, how it was routed, how long each phase
 * took and how it ended. Everything structural lives in `CallReportPage`;
 * this file carries only what makes this report itself.
 */
import type { JSX } from 'react'
import { PhoneCall } from 'lucide-react'

import { CallHistoryDetailDrawer } from '../../../components/reports/calls/CallHistoryDetailDrawer.js'
import CallHistoryAnalytics from '../../../components/reports/calls/CallHistoryAnalytics.js'
import {
  CALL_HISTORY_COLUMNS,
  CALL_HISTORY_REPORT_ID,
} from '../../../components/reports/calls/columns.js'
import CallReportPage from './CallReportPage.js'
import ManualCallUpload from '../../../components/reports/calls/ManualCallUpload.js'

export default function CallHistoryReportPage(): JSX.Element {
  return (
    <CallReportPage
      Icon={PhoneCall}
      title="Call History"
      intro="Every call in a window — routing, timings, outcome and recording"
      unit="call"
      path="/reports/calls/history"
      reportId={CALL_HISTORY_REPORT_ID}
      // v2: recording + transcript columns lead, legacy-portal order.
      viewId={`${CALL_HISTORY_REPORT_ID}.v2`}
      catalog={CALL_HISTORY_COLUMNS}
      fields={['directions', 'phone', 'queues', 'campaigns', 'agents', 'dispositions', 'trunks']}
      windowNote="The window is on the time the call started."
      searchPlaceholder="Search calls…"
      renderAnalytics={(rows) => <CallHistoryAnalytics rows={rows} />}
      emptyDescription="Choose a start date, an end date and an interval in Filters, then run the report."
      noResultsTitle="No calls in this window"
      noResultsDescription="No call was placed or received in the selected scope. Try widening the window, or clearing the queue and agent filters."
      DetailDrawer={CallHistoryDetailDrawer}
      headerActions={(refresh) => <ManualCallUpload onDone={refresh} />}
    />
  )
}
